/**
 * IdentityAgentDetailPageV2 — Phase 6G (P0-IDENT).
 *
 * Agent-scope detail page. Seven sections compose the page, each
 * mapped to a real concern in the SPIFFE/SVID lifecycle:
 *
 *   1. SVID Lifecycle          — serial, expiry, status, Rotate-now
 *   2. Registration Entry      — SPIRE registration YAML + selectors
 *   3. Rotation Override       — inherit vs override (resolved values)
 *   4. Child SVID Policy       — minting allowlist + boundary
 *   5. X.509 Claims            — subject, SAN, AI System extension
 *   6. Lifecycle Events        — timeline of identity events
 *   7. Federation Inheritance  — inherited federation peers + override
 *
 * Sections (3), (4) and (7) carry inherit-vs-override semantics with
 * the resolved source (Organization / Tenant) visible so the user can
 * trace where an effective value came from. Sections (1), (2), (5),
 * (6) display direct agent state.
 *
 * Data
 *   Real fetch against GET /api/v1/identity/agents/{agentId}. The 404
 *   path is honoured (unknown id renders "agent not found"); the 403
 *   path (cross-tenant access) renders the access-denied EmptyState.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Fingerprint, FileText, RefreshCw, Network, KeyRound, Activity, Share2,
  ShieldX, AlertTriangle, ArrowUp, Pencil, Copy, Download, Clock,
} from 'lucide-react';
import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import EntityLabel from '../components/common/EntityLabel';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { EmptyState } from '../components/v2/EmptyState';
import { Skeleton } from '../components/v2/Skeleton';
import { AgentRotationOverrideSlideout } from '../components/v2/identity/AgentRotationOverrideSlideout';
import { AgentChildSvidPolicySlideout } from '../components/v2/identity/AgentChildSvidPolicySlideout';
import { AgentFederationOverrideSlideout } from '../components/v2/identity/AgentFederationOverrideSlideout';
import { getIdentityAgent } from '../services/api';
import AgentIdentityLifecyclePanel from '../components/v2/identity/AgentIdentityLifecyclePanel';

const SVID_STATUS_TONE = {
  active:          'success',
  expiring:        'warning',
  expired:         'danger',
  not_provisioned: 'muted',
};
const SVID_STATUS_LABEL = {
  active:          'Active',
  expiring:        'Expiring soon',
  expired:         'Expired',
  not_provisioned: 'Not provisioned',
};

const TRUST_BAND_TONE = {
  PROBATION:  'warning',
  SUPERVISED: 'cyan',
  ACTIVE:     'success',
  AUTONOMOUS: 'trust',
};

/* ────────────────────────────────────────────────────────────────
   IdentityAgentDetailPageV2 — top-level export
   ──────────────────────────────────────────────────────────────── */

export default function IdentityAgentDetailPageV2() {
  const { agentId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /* Stage 2c-3 — rotation override slideout state. `mode` distinguishes
   * the "add" flow (form with TTL + rotation fraction inputs) from the
   * "remove" flow (confirm dialog → DELETE call). The single state
   * object captures both open/closed and the mode in one update so
   * there's no transient mismatch. */
  const [rotationOverrideSlideout, setRotationOverrideSlideout] =
    useState({ open: false, mode: 'add' });
  /* Stage 3c-3 / 3c-4 — separate state for each slideout so they
   * don't share a single "open" flag (avoids races when the user
   * triggers two in rapid succession). */
  const [childPolicySlideout, setChildPolicySlideout] =
    useState({ open: false, mode: 'add' });
  const [fedOverrideSlideout, setFedOverrideSlideout] =
    useState({ open: false, mode: 'add' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // apiFetch wraps responses as {ok, data} on success and
    // {ok: false, error} on error — it does NOT throw to callers
    // (the .catch block is a defensive backstop, not the main path).
    // We derive HTTP status from the error message text since apiFetch
    // doesn't expose .status: 404 surfaces as "HTTP 404:" or via the
    // Spring exception keyword ("agent_not_found"). Same for 403.
    const classify = (msg) => {
      let status = null;
      const m = /^HTTP (\d{3}):/.exec(msg);
      if (m) status = Number(m[1]);
      else if (/agent_not_found/.test(msg)) status = 404;
      else if (/agent_not_in_tenant_scope/.test(msg)) status = 403;
      return { message: msg, status };
    };
    getIdentityAgent(agentId)
      .then((res) => {
        if (cancelled) return;
        if (res?.ok === false) {
          setError(classify(res.error || 'Identity API error'));
        } else {
          setData(res?.data || null);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(classify(err?.message || String(err)));
          setLoading(false);
        }
      });
  }, [agentId]);

  /* ── Loading state ─────────────────────────────────────────── */
  if (loading) {
    return (
      <V2PageShell
        title="Loading…"
        backTo="/identity/agents"
        backLabel="Back to Agents"
      >
        <div className="flex flex-col gap-3">
          <Skeleton height={80} />
          <Skeleton height={120} />
          <Skeleton height={120} />
          <Skeleton height={120} />
        </div>
      </V2PageShell>
    );
  }

  /* ── Error states — 403 / 404 / generic ────────────────────── */
  if (error) {
    const is403 = error.status === 403;
    const is404 = error.status === 404;
    return (
      <V2PageShell
        title={is404 ? 'Agent not found' : is403 ? 'Access denied' : 'Could not load agent'}
        backTo="/identity/agents"
        backLabel="Back to Agents"
      >
        <Card>
          <EmptyState
            icon={is403 ? <ShieldX /> : <AlertTriangle />}
            title={is404 ? 'No agent with that id'
                  : is403 ? 'This agent is outside your tenant scope'
                  : 'Identity API error'}
            description={is404
              ? `The identifier ${agentId} is not in the registry. It may have been deleted, or the link was hand-edited.`
              : is403
              ? 'Tenant Admins can only view agents inside their own tenant. Switch to the right scope using the avatar menu, or ask a higher-privileged admin to investigate.'
              : error.message}
            primaryAction={
              <Button variant="primary" size="sm" onClick={() => navigate('/identity/agents')}>
                Back to Agents
              </Button>
            }
          />
        </Card>
      </V2PageShell>
    );
  }

  /* ── Happy path ────────────────────────────────────────────── */
  return (
    <V2PageShell
      title={data.name || 'Unnamed agent'}
      subtitle={
        <>
          <span className="font-mono-v2 text-[12px]">{data.svidLifecycle?.spiffeId}</span>
          {' · '}
          {data.archetype} · {data.agentClassification || 'INTERNAL'}
        </>
      }
      backTo="/identity/agents"
      backLabel="Back to Agents"
      tags={
        <>
          {data.trustBand && (
            <Pill tone={TRUST_BAND_TONE[data.trustBand] || 'muted'} size="sm">{data.trustBand}</Pill>
          )}
          <Pill tone="cyan"   size="sm">Agent</Pill>
          <Pill tone="action" size="sm" dot>SPIFFE plane</Pill>
        </>
      }
      actions={
        <>
          <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={14} />}
                  onClick={() => window.location.reload()}>
            Refresh
          </Button>
          <Button variant="primary" size="sm" iconLeft={<KeyRound size={14} />}
                  onClick={() => navigate(`/agents/${data.id}`)}>
            Open in Agent Detail
          </Button>
        </>
      }
    >
      {/* ID-3 — Agent Identity lifecycle (state · credential · timeline · actions).
          Additive; sits above the existing seven-section detail. */}
      <AgentIdentityLifecyclePanel agentId={agentId} />

      {/* Two-column grid for the seven sections.
       * Sections 1-2 (SVID, Registration) and 3-4 (Rotation, Child) and
       * 5-6 (X.509, Events) sit in 2-col pairs; Federation gets full width. */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <SvidLifecycleSection      data={data.svidLifecycle}   />
        <RegistrationSection       data={data.registration}    spiffeId={data.svidLifecycle?.spiffeId} />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <InheritOrOverrideSection
          title="Rotation override"
          icon={<RefreshCw size={16} className="text-v2-warning" aria-hidden="true" />}
          subtitle="Per-agent SVID rotation policy"
          data={data.rotationOverride}
          renderResolved={(r) => (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-[12px]">
              <Fact label="SVID TTL"        value={r.svidTtl} />
              <Fact label="Rotation window" value={r.rotationWindow} />
              <Fact label="JWT-SVID TTL"    value={r.jwtSvidTtl} />
            </div>
          )}
          // Stage 2c-3 — wire the footer button to the rotation
          // override slideout. mode='add' opens the form; mode='remove'
          // opens the confirm dialog.
          onOverrideAction={(mode) => setRotationOverrideSlideout({ open: true, mode })}
        />
        <ChildSvidPolicySection
          data={data.childSvidPolicy}
          onEditAction={(mode) => setChildPolicySlideout({ open: true, mode })}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <X509ClaimsSection      data={data.x509Claims} />
        <LifecycleEventsSection events={data.lifecycleEvents} />
      </div>

      <FederationInheritanceSection
        data={data.federationInheritance}
        onOverrideAction={(mode) => setFedOverrideSlideout({ open: true, mode })}
      />

      {/* Stage 2c-3 — rotation override slideout (add + remove modes). */}
      <AgentRotationOverrideSlideout
        isOpen={rotationOverrideSlideout.open}
        mode={rotationOverrideSlideout.mode}
        agentId={data.id}
        agentName={data.name}
        onClose={() => setRotationOverrideSlideout({ open: false, mode: 'add' })}
        onSaved={() => { window.location.reload(); }}
      />

      {/* Stage 3c-3 — child SVID policy slideout. Pre-fills from the
          current override row's values so edits start from the
          existing state rather than blank. */}
      <AgentChildSvidPolicySlideout
        isOpen={childPolicySlideout.open}
        mode={childPolicySlideout.mode}
        agentId={data.id}
        agentName={data.name}
        initialAllowChildren={!!data.childSvidPolicy?.allowChildren}
        initialMaxChildren={data.childSvidPolicy?.maxChildren || 0}
        initialAllowedSpiffeIds={data.childSvidPolicy?.allowedSpiffeIds || []}
        initialTrustBoundary={data.childSvidPolicy?.trustBoundary || 'tenant-bounded'}
        onClose={() => setChildPolicySlideout({ open: false, mode: 'add' })}
        onSaved={() => { window.location.reload(); }}
      />

      {/* Stage 3c-4 — agent federation override slideout. Loads the
          available peer list from /api/v1/identity/federation-peers
          on open and renders a multi-select. */}
      <AgentFederationOverrideSlideout
        isOpen={fedOverrideSlideout.open}
        mode={fedOverrideSlideout.mode}
        agentId={data.id}
        agentName={data.name}
        initialPeerIds={data.federationInheritance?.override?.peerIds || []}
        onClose={() => setFedOverrideSlideout({ open: false, mode: 'add' })}
        onSaved={() => { window.location.reload(); }}
      />
    </V2PageShell>
  );
}

/* ────────────────────────────────────────────────────────────────
   Section 1 — SVID Lifecycle.
   ──────────────────────────────────────────────────────────────── */

function SvidLifecycleSection({ data }) {
  if (!data) return null;
  const statusTone = SVID_STATUS_TONE[data.status] || 'muted';
  const statusLabel = SVID_STATUS_LABEL[data.status] || data.status;
  return (
    <Card>
      <Card.Header
        title={<><Fingerprint size={16} className="inline -mt-0.5 mr-1.5 text-v2-action" aria-hidden="true" />SVID lifecycle</>}
        subtitle="X.509-SVID serial, expiry, and current status"
        actions={<Pill tone={statusTone} size="sm">{statusLabel}</Pill>}
      />
      <Card.Body>
        <div className="grid grid-cols-2 gap-3 text-[12px]">
          <Fact label="Serial"        value={data.serial    || <span className="text-v2-ink-muted">Not minted yet</span>} mono />
          <Fact label="Trust domain"  value={data.trustDomain} mono />
          <Fact label="Issued at"     value={data.issuedAt  ? new Date(data.issuedAt).toLocaleString()  : <span className="text-v2-ink-muted">—</span>} />
          <Fact label="Expires at"    value={data.expiresAt ? new Date(data.expiresAt).toLocaleString() : <span className="text-v2-ink-muted">—</span>} />
        </div>
        <div className="mt-3 pt-3 border-t border-v2-border-subtle">
          <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">SPIFFE ID</div>
          <div className="font-mono-v2 text-[12px] text-v2-ink break-all">{data.spiffeId}</div>
        </div>
      </Card.Body>
      <Card.Footer>
        <Button variant="secondary" size="xs" iconLeft={<RefreshCw size={12} />}
                disabled={data.status === 'not_provisioned'}>
          Rotate now
        </Button>
        <Button variant="ghost" size="xs" iconLeft={<Copy size={12} />}
                onClick={() => navigator.clipboard?.writeText(data.spiffeId || '')}>
          Copy SPIFFE ID
        </Button>
      </Card.Footer>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────
   Section 2 — Registration Entry.
   ──────────────────────────────────────────────────────────────── */

function RegistrationSection({ data, spiffeId }) {
  if (!data) return null;
  const yamlAvailable = !!data.yaml;
  return (
    <Card>
      <Card.Header
        title={<><FileText size={16} className="inline -mt-0.5 mr-1.5 text-v2-trust" aria-hidden="true" />Registration entry</>}
        subtitle="SPIRE registration record bound to this agent"
      />
      <Card.Body>
        <div className="grid grid-cols-1 gap-2 text-[12px] mb-3">
          {/* RC-25 / R1 — Parent renders via EntityLabel for the
              "{name} ({shortid})" composition. The federation registration
              payload doesn't carry the parent's name, so this falls back
              to "agent ({shortid})" per R3 — which is the same visual
              affordance as before, just with the platform-standard format
              and a tooltip exposing the full UUID for cross-system lookup. */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">Parent</div>
            <EntityLabel kind="agent" id={data.parentId} size="md" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">Selectors</div>
            <div className="flex flex-wrap gap-1">
              {(data.selectors || []).map((s) => (
                <Pill key={s} tone="muted" size="sm">{s}</Pill>
              ))}
            </div>
          </div>
        </div>
        {yamlAvailable ? (
          <div className="bg-v2-muted rounded-v2-sm px-3 py-2 text-[11px] font-mono-v2 text-v2-ink whitespace-pre-wrap max-h-[180px] overflow-auto">
            {data.yaml}
          </div>
        ) : (
          <div className="bg-v2-warning-soft border border-v2-warning/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-warning">
            <AlertTriangle size={13} className="inline -mt-0.5 mr-1.5" aria-hidden="true" />
            SPIRE registration YAML is unavailable. Configure
            <span className="font-mono-v2 mx-1">agentlets.spiffe.trust-domain</span>
            to enable.
          </div>
        )}
      </Card.Body>
      <Card.Footer>
        <Button
          variant="secondary"
          size="xs"
          iconLeft={<Download size={12} />}
          disabled={!yamlAvailable}
        >
          Download YAML
        </Button>
        <Button
          variant="ghost"
          size="xs"
          iconLeft={<Copy size={12} />}
          disabled={!yamlAvailable}
          onClick={() => navigator.clipboard?.writeText(data.yaml || '')}
        >
          Copy
        </Button>
      </Card.Footer>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────
   Generic — inherit-vs-override section card.
   Used by Rotation override; the same pattern repeats for Federation
   below with a different `renderResolved` callback.
   ──────────────────────────────────────────────────────────────── */

function InheritOrOverrideSection({ title, icon, subtitle, data, renderResolved, onOverrideAction }) {
  if (!data) return null;
  // Stage 2c-3 — when onOverrideAction is supplied, the footer button
  // becomes interactive and calls it with mode='add' or mode='remove'
  // based on the current inherit state. Sections that haven't been
  // wired to a slideout yet (e.g. federation inheritance) just omit
  // the prop and the button stays disabled — backward compatible.
  const interactive = typeof onOverrideAction === 'function';
  return (
    <Card>
      <Card.Header
        title={<>{icon} {title}</>}
        subtitle={subtitle}
        actions={
          data.inherit
            ? <Pill tone="action" size="sm"><ArrowUp size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />Inherited</Pill>
            : <Pill tone="trust"  size="sm"><Pencil size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />Override</Pill>
        }
      />
      <Card.Body>
        <div className="text-[11px] text-v2-ink-secondary mb-2">
          {data.inherit
            ? <>Resolved from <strong className="text-v2-ink">{data.resolvedSource}</strong> defaults</>
            : <>Local override · was inheriting from <strong>{data.resolvedSource}</strong></>}
        </div>
        {renderResolved(data.resolvedValues || {})}
      </Card.Body>
      <Card.Footer>
        <Button
          variant="secondary"
          size="xs"
          disabled={!interactive}
          onClick={interactive ? () => onOverrideAction(data.inherit ? 'add' : 'remove') : undefined}
        >
          {data.inherit ? 'Add override' : 'Remove override'}
        </Button>
      </Card.Footer>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────
   Section 4 — Child SVID Policy.
   ──────────────────────────────────────────────────────────────── */

function ChildSvidPolicySection({ data, onEditAction }) {
  if (!data) return null;
  // Stage 3c-3 — wire footer button when caller supplies onEditAction.
  const interactive = typeof onEditAction === 'function';
  return (
    <Card>
      <Card.Header
        title={<><Network size={16} className="inline -mt-0.5 mr-1.5 text-v2-success" aria-hidden="true" />Child SVID policy</>}
        subtitle="Whether this agent can mint identities for sub-agents"
        actions={data.allowChildren
          ? <Pill tone="success" size="sm">Enabled</Pill>
          : <Pill tone="muted"   size="sm">Disabled</Pill>}
      />
      <Card.Body>
        <div className="grid grid-cols-2 gap-3 text-[12px]">
          <Fact label="Children minted" value={`${data.currentChildCount} / ${data.maxChildren || '∞'}`} />
          <Fact label="Trust boundary"  value={data.trustBoundary} />
        </div>
        <div className="mt-3">
          <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">
            Allowed child SPIFFE IDs
          </div>
          {data.allowedSpiffeIds && data.allowedSpiffeIds.length > 0 ? (
            <div className="flex flex-col gap-1">
              {data.allowedSpiffeIds.map((s) => (
                <div key={s} className="font-mono-v2 text-[11.5px] text-v2-ink">{s}</div>
              ))}
            </div>
          ) : (
            <div className="text-[12px] text-v2-ink-muted">None — this agent cannot mint child SVIDs</div>
          )}
        </div>
      </Card.Body>
      {interactive && (
        <Card.Footer>
          <Button variant="secondary" size="xs"
                  onClick={() => onEditAction('add')}>
            Edit policy
          </Button>
          {data.allowChildren && (
            <Button variant="ghost" size="xs"
                    onClick={() => onEditAction('remove')}>
              Remove override
            </Button>
          )}
        </Card.Footer>
      )}
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────
   Section 5 — X.509 Claims.
   ──────────────────────────────────────────────────────────────── */

function X509ClaimsSection({ data }) {
  if (!data) return null;
  const ai = data.aiSystemExtension || {};
  return (
    <Card>
      <Card.Header
        title={<><KeyRound size={16} className="inline -mt-0.5 mr-1.5 text-v2-warning" aria-hidden="true" />X.509 claims</>}
        subtitle="Subject DN + AI System extension (carried by every SVID)"
      />
      <Card.Body>
        <div className="text-[12px] mb-3">
          <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">Subject</div>
          <div className="font-mono-v2 text-v2-ink break-all">{data.subject}</div>
        </div>
        <div className="text-[12px] mb-3">
          <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">SAN (URI)</div>
          {(data.sanList || []).map((s) => (
            <div key={s} className="font-mono-v2 text-v2-ink break-all">{s}</div>
          ))}
        </div>
        <div className="pt-3 border-t border-v2-border-subtle">
          <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-2">
            AI System extension (1.3.6.1.4.1.59999.1)
          </div>
          <div className="grid grid-cols-2 gap-2 text-[12px]">
            <Fact label="Function mode"  value={ai.functionMode  || '—'} />
            <Fact label="Industry mode"  value={ai.industryMode  || '—'} />
            <Fact label="Operation mode" value={ai.operationMode || '—'} />
            <Fact label="Execution mode" value={ai.executionMode || '—'} />
            <Fact label="Trust band"     value={ai.trustBand     || '—'} />
            <Fact label="Archetype"      value={ai.archetype     || '—'} />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────
   Section 6 — Lifecycle Events timeline.
   ──────────────────────────────────────────────────────────────── */

const EVENT_TONE = {
  REGISTERED:  'action',
  SVID_MINTED: 'success',
  ROTATED:     'success',
  REVOKED:     'danger',
  FEDERATED:   'trust',
};

function LifecycleEventsSection({ events = [] }) {
  return (
    <Card>
      <Card.Header
        title={<><Activity size={16} className="inline -mt-0.5 mr-1.5 text-v2-cyan" aria-hidden="true" />Lifecycle events</>}
        subtitle="Identity events recorded against this agent"
      />
      <Card.Body>
        {events.length === 0 ? (
          <div className="text-[12px] text-v2-ink-muted py-3 text-center">
            No lifecycle events yet
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {events.map((e, i) => (
              <div key={i} className="flex items-start gap-3 pb-2 border-b border-v2-border-subtle last:border-b-0 last:pb-0">
                <Pill tone={EVENT_TONE[e.type] || 'muted'} size="sm">{e.type}</Pill>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] text-v2-ink">{e.detail}</div>
                  <div className="text-[11px] text-v2-ink-muted flex items-center gap-1.5 mt-0.5">
                    <Clock size={11} aria-hidden="true" />
                    <span>{new Date(e.when).toLocaleString()}</span>
                    <span>·</span>
                    <span>{e.actor}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────
   Section 7 — Federation Inheritance.
   ──────────────────────────────────────────────────────────────── */

function FederationInheritanceSection({ data, onOverrideAction }) {
  if (!data) return null;
  // Stage 3c-4 — footer button wires to slideout when caller supplies callback.
  const interactive = typeof onOverrideAction === 'function';
  return (
    <Card>
      <Card.Header
        title={<><Share2 size={16} className="inline -mt-0.5 mr-1.5 text-v2-trust" aria-hidden="true" />Federation inheritance</>}
        subtitle="Which federated trust domains this agent can talk to"
        actions={
          data.inherit
            ? <Pill tone="action" size="sm"><ArrowUp size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />Inherited</Pill>
            : <Pill tone="trust"  size="sm"><Pencil size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />Override</Pill>
        }
      />
      <Card.Body>
        <div className="text-[11px] text-v2-ink-secondary mb-2">
          {data.inherit
            ? <>Resolved from <strong className="text-v2-ink">{data.resolvedSource}</strong> federation list</>
            : <>Local override — overrides {data.resolvedSource} list</>}
        </div>
        <div className="flex flex-col gap-1.5">
          {(data.inheritedPeers || []).map((p) => (
            <div key={p.spiffeId} className="flex items-center gap-3 px-3 py-2 border border-v2-border-subtle rounded-v2-md">
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-v2-ink">{p.name}</div>
                <div className="font-mono-v2 text-[11px] text-v2-ink-muted truncate">{p.spiffeId}</div>
              </div>
              <Pill tone="success" size="sm">{p.direction}</Pill>
            </div>
          ))}
        </div>
      </Card.Body>
      <Card.Footer>
        <Button
          variant="secondary"
          size="xs"
          disabled={!interactive}
          onClick={interactive ? () => onOverrideAction(data.inherit ? 'add' : 'remove') : undefined}>
          {data.inherit ? 'Add agent-level override' : 'Remove override'}
        </Button>
      </Card.Footer>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────
   Fact — label-above-value microcomponent. The bread and butter of
   the entire detail page.
   ──────────────────────────────────────────────────────────────── */

function Fact({ label, value, mono = false }) {
  return (
    <div className="min-w-0">
      <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold">{label}</div>
      <div className={'text-[12.5px] text-v2-ink mt-0.5 break-words ' + (mono ? 'font-mono-v2' : '')}>
        {value || '—'}
      </div>
    </div>
  );
}
